import { Apollo } from 'apollo-angular';
import { Observable } from 'rxjs';
import gql from 'graphql-tag';
import { map } from 'rxjs/operators';
import {FetchPolicy} from '@apollo/client/core';
import * as _ from 'lodash';

export interface Criteria { [K: string]: any; }
export type MutationParam = { [K: string]: any } | string | boolean;
export interface MutationParams { [K: string]: MutationParam; }
export interface Fragment {
    name: string;
    on: string;
    fields: string[];
}

export abstract class AbstractService {

    protected constructor(protected apollo: Apollo,
                          protected domainName: string,
                          protected entityName: string,
                          protected debugMode?: boolean) {
    }

    protected query(queryName: string, criteria?: Criteria, projection: string[] = [], fragments: Fragment[] = [],
                    policy: FetchPolicy = 'no-cache'): Observable<any> {
        const criteriaAsString = this.criteriaToGraphQL(criteria);
        const queryParams = criteriaAsString.length === 0 ? '' : `(${criteriaAsString})`;
        let query = `
            query {
                ${this.domainName} {
                    ${this.entityName} {
                       ${queryName} ${queryParams} ${projection.length > 0 ? '{' : ''}
                           ${projection.join('\n')}
                       ${projection.length > 0 ? '}' : ''}
                    }
                 }
            }
            ${this.fragmentsToGraphQL(fragments)}
        `;
        if (queryName != null && criteria == null &&
            projection.length === 0 && fragments.length === 0) {
            query = `
            query {
                ${this.domainName} {
                    ${this.entityName} {
                       ${queryName}
                    }
                }
            }
        `;
        }
        if (this.debugMode) {
            console.log('query', query);
        }
        return this.apollo
            .query<any>({
                query: gql(query),
                fetchPolicy: policy
            });
    }

    protected create(param: MutationParam, projection: string[]): Observable<any> {
        const params = {};
        params[this.entityName] = param;
        return this.mutate(`create${this.capitalize(this.entityName)}`, params, projection);
    }

    protected update(param: MutationParam, projection: string[]): Observable<any> {
        const params = {};
        params[this.entityName] = param;
        return this.mutate(`update${this.capitalize(this.entityName)}`, params, projection);
    }

    protected mutate<R>(mutationName: string, params: MutationParams, projection: string[], fragments: Fragment[] = []): Observable<R> {
        const command = `
            mutation {
                ${this.domainName} {
                    ${this.entityName} {
                        ${mutationName} (${this.paramsToGraphQL(params)}) {
                            ${projection.join(', ')}
                        }
                    }
                }
            }
            ${this.fragmentsToGraphQL(fragments)}
        `;
        if (this.debugMode) {
            console.log('mutation', command);
        }
        return this.apollo
            .mutate<any>({
                mutation: gql(command)
            })
            .pipe(
                map(result => {
                    if (result.errors && !result.data) {
                        throw result.errors[0];
                    }
                    return result.data[this.domainName][this.entityName][mutationName];
                })
            );
    }

    private criteriaToGraphQL(criteria: Criteria): string {
        if (!criteria) {
            return '';
        }
        return Object.keys(criteria)
            .filter(key => criteria[key] !== undefined && criteria[key] !== null)
            .map(key => `${key}: ${this.transformValue(criteria[key])}`)
            .join(',');
    }

    private paramsToGraphQL(params: MutationParams): string {
        if (!params) {
            return '';
        }
        return Object.keys(params)
            .filter(key => params[key] !== undefined && params[key] !== null)
            .map(key => {
                const param = params[key];
                if (typeof param === 'string' || typeof param === 'boolean' || typeof param === 'number' || Array.isArray(param)) {
                    return `${key}: ${JSON.stringify(param)}`;
                } else {
                    const paramValues = Object.keys(param)
                        .filter(key => !key.startsWith('__'))
                        .filter(key => param[key] !== undefined && param[key] !== null)
                        .map(key => `${key}: ${this.transformValue(param[key])}`)
                        .join(',');
                    return `${key}: { ${paramValues} }`;
                }
            })
            .join(', ');
    }

    private capitalize(str: string): string {
        if (!str) { return str; }
        return str[0].toUpperCase() + str.substr(1);
    }

    private transformValue(value: any) {
        if (_.isObject(value)) {
            Object.keys(value)
                .filter(key => _.isArray(value[key]) || _.isObject(value[key]))
                .forEach(key => this.transformValue(value[key]));
            Object.keys(value)
                .filter(key => key.startsWith('__'))
                .forEach(key => delete value[key]);
        } else if (_.isArray(value)) {
            value.forEach(item => this.transformValue(item));
        }
        // uggly ...
        return JSON.stringify(value)
            .replace(/\"([^(\")"]+)\":/g, '$1:');
    }

    private fragmentsToGraphQL(fragments: Fragment[]): string {
        return fragments
            .map(fragment => {
                const fields = fragment.fields.map(field => field).join('\n');
                return `fragment ${fragment.name} on ${fragment.on} {
                    ${fields}
                }`;
            })
            .join('\n');
    }
}
